import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, IconButton, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, Divider, Tabs, Tab
} from '@mui/material';
import { Visibility as ViewIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getEvents, deleteEvent } from '../../services/api';
import ConfirmDeleteDialog from '../../components/Common/ConfirmDeleteDialog';

// Default Leaflet marker icons reference image files that don't survive bundling —
// point them at the CDN copies instead, otherwise markers render as broken images.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

const colWidths = { org: 140, group: 140, phone: 120, date: 130, time: 100, lat: 120, lng: 120 };

const formatDate = (value) => value ? new Date(value).toLocaleDateString('en-GB') : '-';
const formatDateTime = (value) => value ? new Date(value).toLocaleString('en-GB') : '-';

const Events = () => {
  const { groupId } = useParams();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [viewing, setViewing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [tab, setTab] = useState('current');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEvents(groupId);
      if (res.success) setEvents(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  // Events can arrive from the live consumer app or another admin, not just this
  // browser tab — poll quietly in the background so new/changed/deleted rows show
  // up without the user having to refresh. No spinner, no error toast on failure
  // (a single missed poll isn't worth interrupting the user over).
  useEffect(() => {
    const interval = setInterval(() => {
      getEvents(groupId)
        .then(res => { if (res.success) setEvents(res.data); })
        .catch(err => console.error('Background event refresh failed:', err.message));
    }, 15000);
    return () => clearInterval(interval);
  }, [groupId]);

  const requestRemove = (ev) => setConfirmDelete(ev);

  const handleConfirmDelete = async () => {
    const ev = confirmDelete;
    setConfirmDelete(null);
    try {
      await deleteEvent(groupId, ev.id);
      setEvents(prev => prev.filter(e => e.id !== ev.id));
      setViewing(null);
      setSuccess('Event removed');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove event');
    }
  };

  const hasCoords = (ev) => ev && ev.lat && ev.lang && !isNaN(parseFloat(ev.lat)) && !isNaN(parseFloat(ev.lang));

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentEvents = events.filter(ev => ev.event_date && new Date(ev.event_date) >= today);
  const expiredEvents = events.filter(ev => !ev.event_date || new Date(ev.event_date) < today);
  const visibleEvents = tab === 'current' ? currentEvents : expiredEvents;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1 }}>
        <Typography variant="h4" fontWeight={600}>Events</Typography>
        {!loading && (
          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            ({events.length})
          </Typography>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, minHeight: 0 }}>
          <Tab value="current" label={`Current (${currentEvents.length})`} sx={{ minHeight: 0 }} />
          <Tab value="expired" label={`Expired (${expiredEvents.length})`} sx={{ minHeight: 0 }} />
        </Tabs>

        {loading ? (
          <Typography variant="body2" color="text.secondary">Loading...</Typography>
        ) : visibleEvents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No {tab === 'current' ? 'current' : 'expired'} events found
          </Typography>
        ) : (
          <>
            {/* Column headers */}
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ width: colWidths.org }}>Organisation</Typography>
              <Typography variant="subtitle2" sx={{ width: colWidths.group }}>Group</Typography>
              <Typography variant="subtitle2" sx={{ width: colWidths.phone }}>Ph No</Typography>
              <Typography variant="subtitle2" sx={{ width: colWidths.date }}>Event Date</Typography>
              <Typography variant="subtitle2" sx={{ width: colWidths.time }}>Event Time</Typography>
              <Typography variant="subtitle2" sx={{ width: colWidths.lat }}>Latitude</Typography>
              <Typography variant="subtitle2" sx={{ width: colWidths.lng }}>Longitude</Typography>
              <Typography variant="subtitle2">Action</Typography>
            </Box>

            {visibleEvents.map((ev) => (
              <Box key={ev.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, borderBottom: '1px solid #f0f0f0' }}>
                <Typography variant="body2" sx={{ width: colWidths.org }}>{ev.organisation_name || '-'}</Typography>
                <Typography variant="body2" sx={{ width: colWidths.group }}>{ev.group_name || '-'}</Typography>
                <Typography variant="body2" sx={{ width: colWidths.phone }}>{ev.admin_phone || '-'}</Typography>
                <Typography variant="body2" sx={{ width: colWidths.date }}>{formatDate(ev.event_date)}</Typography>
                <Typography variant="body2" sx={{ width: colWidths.time }}>{ev.event_time || '-'}</Typography>
                <Typography variant="body2" sx={{ width: colWidths.lat }}>{ev.lat || '-'}</Typography>
                <Typography variant="body2" sx={{ width: colWidths.lng }}>{ev.lang || '-'}</Typography>
                <IconButton size="small" color="success" onClick={() => setViewing(ev)}>
                  <ViewIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => requestRemove(ev)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </>
        )}
      </Paper>

      {/* View event details */}
      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="md" fullWidth>
        <DialogTitle>Event Details</DialogTitle>
        <DialogContent>
          {viewing && (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, py: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Event Information</Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="caption" color="text.secondary">ORGANISATION</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{viewing.organisation_name || '-'}</Typography>
                <Typography variant="caption" color="text.secondary">GROUP</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{viewing.group_name || '-'}</Typography>
                <Typography variant="caption" color="text.secondary">ADMIN PHONE</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{viewing.admin_phone || '-'}</Typography>
                <Typography variant="caption" color="text.secondary">EVENT DATE &amp; TIME</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{formatDate(viewing.event_date)} at {viewing.event_time || '-'}</Typography>
                <Typography variant="caption" color="text.secondary">COORDINATES</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {hasCoords(viewing) ? `${viewing.lat} / ${viewing.lang}` : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">CREATED</Typography>
                <Typography variant="body2">{formatDateTime(viewing.added)}</Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Event Description</Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body2">{viewing.event_desc || '-'}</Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Event Location</Typography>
                <Divider sx={{ mb: 1.5 }} />
                {hasCoords(viewing) ? (
                  <Box sx={{ height: 260, borderRadius: 1, overflow: 'hidden' }}>
                    <MapContainer
                      center={[parseFloat(viewing.lat), parseFloat(viewing.lang)]}
                      zoom={14}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        attribution='&copy; OpenStreetMap contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={[parseFloat(viewing.lat), parseFloat(viewing.lang)]}>
                        <Popup>
                          {formatDate(viewing.event_date)} at {viewing.event_time || '-'}
                        </Popup>
                      </Marker>
                    </MapContainer>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No location recorded</Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!confirmDelete}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      <Snackbar
        open={!!success || !!error}
        autoHideDuration={5000}
        onClose={() => { setSuccess(''); setError(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setSuccess(''); setError(''); }}
          severity={success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Events;
